import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  IconButton,
  Typography,
  Button,
  Collapse,
  Tooltip,
  Checkbox,
  CircularProgress,
  Alert,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import InputField from "../../Components/InputField";
import CustomDatePicker from "../../Components/CustomDatePicker";
import StepperTabs from "../ManagePricelists/Modals/PricelistModal/content/StepperTabs";
import ProductModal from "../ManagePricelists/Content/ProductsContent/ProductModal";
import { apiGetRequest, apiPostRequest, apiPutRequest } from "../../Helpers/Requests";
import endpoints from "../../Helpers/Endpoints";
import { useSnackbar } from "../../Components/SnackBar";
import {
  CURRENCY,
  keyOf,
  sameRef,
  fmtDate,
  toLocalISO,
  valueLabel,
  autoTitle,
  uiType,
  bundleStyleOf,
} from "./hatavotUtils";

const FAR_FUTURE = new Date("2100-01-01");
// Item types a supplier can create from the deal modal — services are
// CommunitMe-managed pricelists and can't be created here.
const CREATABLE_TYPES = ["products", "restaurants"];
const EMPTY_PICKER = { itemTypes: [], categories: {}, items: [], communities: [] };

const useTokens = () => useTheme().palette.customColors;
const labelSx = (c) => ({ fontSize: "15px", fontWeight: 500, color: c.textStrong });
const hintSx = (c) => ({ fontSize: "12.5px", color: c.textSoft });

const FieldLabel = ({ children, sx }) => {
  const c = useTokens();
  return <Typography sx={{ ...labelSx(c), marginBottom: "8px", ...sx }}>{children}</Typography>;
};

const HelpTip = ({ text }) => {
  const c = useTokens();
  return (
    <Tooltip
      arrow
      placement="top"
      title={text}
      componentsProps={{
        tooltip: { sx: { backgroundColor: c.tooltipBg, padding: "10px", borderRadius: "8px", maxWidth: 280, fontSize: "13px", lineHeight: 1.5 } },
        arrow: { sx: { color: c.tooltipBg } },
      }}
    >
      <Box component="span" sx={{ display: "inline-flex", cursor: "pointer" }}>
        <InfoOutlinedIcon sx={{ fontSize: 16, color: c.textFaint }} />
      </Box>
    </Tooltip>
  );
};

const LabelWithHelp = ({ children, help }) => {
  const c = useTokens();
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px" }}>
      <Typography sx={labelSx(c)}>{children}</Typography>
      <HelpTip text={help} />
    </Box>
  );
};

const Segmented = ({ value, onChange, options }) => {
  const c = useTokens();
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
      {options.map((o) => {
        const on = value === o.value;
        return (
          <Box key={o.value} component="button" type="button" onClick={() => onChange(o.value)}
            sx={{ cursor: "pointer", fontFamily: "inherit", fontSize: "14px", fontWeight: on ? 600 : 500, padding: "8px 14px", borderRadius: "8px", border: `1px solid ${on ? c.brand : c.borderMuted}`, backgroundColor: on ? c.brandSoft : c.surface, color: on ? c.brand : c.textBody, transition: "all .12s ease" }}>
            {o.label}
          </Box>
        );
      })}
    </Box>
  );
};

const Pill = ({ on, onClick, children }) => {
  const c = useTokens();
  return (
    <Box component="button" type="button" onClick={onClick}
      sx={{ cursor: "pointer", fontFamily: "inherit", fontSize: "13px", padding: "6px 11px", borderRadius: "8px", border: `1px solid ${on ? c.brand : c.borderMuted}`, backgroundColor: on ? c.brandSoft : c.surface, color: on ? c.brand : c.textBody }}>
      {children}
    </Box>
  );
};

const ItemThumb = ({ item, size = 36 }) => {
  const c = useTokens();
  const { t } = useTranslation("hatavot");
  return item.image ? (
    <Box component="img" src={item.image} alt="" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
      sx={{ width: size, height: size, borderRadius: "6px", objectFit: "cover", flex: "none", border: `1px solid ${c.border}` }} />
  ) : (
    <Box sx={{ width: size, height: size, borderRadius: "6px", flex: "none", backgroundColor: c.brandSoftStrong, display: "grid", placeItems: "center", color: c.brand, fontSize: "12px", fontWeight: 700 }}>
      {t(`item_type.${item.type}`).charAt(0)}
    </Box>
  );
};

// Search the supplier's catalog (picker items) and add items one by one.
const SearchAddPicker = ({ items, onAdd, excludeKeys, categoryName }) => {
  const c = useTokens();
  const { t } = useTranslation("hatavot");
  const [q, setQ] = useState("");
  const term = q.trim();
  const results = term
    ? items
        .filter((it) => !excludeKeys.includes(keyOf(it)))
        .filter((it) => it.name.includes(term) || t(`item_type.${it.type}`).includes(term) || String(it.id).includes(term))
        .slice(0, 8)
    : [];
  return (
    <Box sx={{ position: "relative" }}>
      <InputField name="itemSearch" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("modal.search.placeholder")}
        InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 20, color: c.textFaint, ml: "10px" }} /> }} />
      {term && (
        <Box sx={{ position: "absolute", zIndex: 5, top: "calc(100% + 4px)", insetInline: 0, backgroundColor: c.surface, border: `1px solid ${c.border}`, borderRadius: "8px", boxShadow: c.shadow, overflow: "hidden auto", maxHeight: "260px" }}>
          {results.length === 0 && (
            <Typography sx={{ ...hintSx(c), padding: "10px 12px" }}>{t("modal.search.no_results")}</Typography>
          )}
          {results.map((it) => (
            <Box key={keyOf(it)} component="button" type="button" onClick={() => { onAdd({ type: it.type, id: it.id }); setQ(""); }}
              sx={{ cursor: "pointer", width: "100%", textAlign: "start", background: "none", border: "none", fontFamily: "inherit", display: "flex", alignItems: "center", gap: "10px", padding: "8px 12px", "&:hover": { backgroundColor: c.brandSoft } }}>
              <ItemThumb item={it} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: "14px", color: c.textStrong }}>{it.name}</Typography>
                <Typography sx={{ fontSize: "11.5px", color: c.textSoft }}>#{it.id} · {t(`item_type.${it.type}`)}{categoryName(it.type, it.categoryId) ? ` · ${categoryName(it.type, it.categoryId)}` : ""}</Typography>
              </Box>
              {it.price != null && <Typography sx={{ fontSize: "13px", fontWeight: 600, color: c.textMedium }}>{CURRENCY}{it.price}</Typography>}
              <AddIcon sx={{ fontSize: 18, color: c.brand }} />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

// "פריט חדש" — opens the REAL product/restaurant creation modal (the one the
// Products tab uses) for the chosen vertical. The created item is added to the
// deal by the parent.
const NewItemPanel = ({ itemTypes, vertical, setVertical, onOpen, loading }) => {
  const c = useTokens();
  const { t } = useTranslation("hatavot");
  const creatable = CREATABLE_TYPES.filter((k) => itemTypes.includes(k));
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "14px", padding: "16px", border: `1px dashed ${c.border}`, borderRadius: "10px" }}>
      {creatable.length === 0 ? (
        <Typography sx={hintSx(c)}>{t("modal.new_item.unavailable")}</Typography>
      ) : (
        <>
          <Typography sx={hintSx(c)}>{t("modal.new_item.hint")}</Typography>
          <Box>
            <FieldLabel>{t("modal.new_item.vertical_label")}</FieldLabel>
            <Segmented value={vertical} onChange={setVertical} options={creatable.map((k) => ({ value: k, label: t(`item_type.${k}`) }))} />
          </Box>
          <Box>
            <Button onClick={onOpen} disabled={loading} variant="outlined" startIcon={loading ? <CircularProgress size={16} sx={{ color: c.brand }} /> : <AddIcon />}
              sx={{ textTransform: "none", borderRadius: "8px", color: c.brand, borderColor: c.brand, "&:hover": { borderColor: c.brand, backgroundColor: c.brandSoft } }}>
              {t("modal.new_item.open")}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

// Hierarchical target picker — everything, or a custom mix. "כל ה___" lives
// inside each type's accordion as its first option, mutually exclusive with
// that type's specific categories.
const TargetPicker = ({ mode, setMode, picker, selTypes, selCats, toggleWholeType, toggleCat, selItems, addItem, categoryName }) => {
  const c = useTokens();
  const { t } = useTranslation("hatavot");
  const [expanded, setExpanded] = useState([]);
  const toggleExpand = (k) => setExpanded((e) => (e.includes(k) ? e.filter((x) => x !== k) : [...e, k]));
  return (
    <Box>
      <FieldLabel>{t("modal.targets.label")}</FieldLabel>
      <Segmented value={mode} onChange={setMode} options={[{ value: "everything", label: t("modal.targets.everything") }, { value: "custom", label: t("modal.targets.custom") }]} />
      <Collapse in={mode === "custom"} unmountOnExit>
        <Box sx={{ marginTop: "14px", display: "flex", flexDirection: "column", gap: "8px" }}>
          <FieldLabel sx={{ marginBottom: "2px" }}>{t("modal.targets.categories_label")}</FieldLabel>
          {picker.itemTypes.map((typeKey) => {
            const label = t(`item_type.${typeKey}`);
            const open = expanded.includes(typeKey);
            const wholeOn = selTypes.includes(typeKey);
            return (
              <Box key={typeKey} sx={{ border: `1px solid ${c.border}`, borderRadius: "10px", overflow: "hidden" }}>
                <Box component="button" type="button" onClick={() => toggleExpand(typeKey)}
                  sx={{ width: "100%", cursor: "pointer", background: "none", border: "none", fontFamily: "inherit", display: "flex", alignItems: "center", gap: "10px", padding: "11px 12px" }}>
                  <Typography sx={{ ...labelSx(c), flex: 1, textAlign: "start" }}>{label}</Typography>
                  {wholeOn && <Box sx={{ fontSize: "11px", color: c.brand, backgroundColor: c.brandSoft, padding: "2px 8px", borderRadius: "6px" }}>{t("target.all_of_type", { type: label })}</Box>}
                  <ExpandMoreIcon sx={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .15s", color: c.iconMuted }} />
                </Box>
                <Collapse in={open} unmountOnExit>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", padding: "0 12px 12px" }}>
                    <Pill on={wholeOn} onClick={() => toggleWholeType(typeKey)}>{t("target.all_of_type", { type: label })}</Pill>
                    {(picker.categories[typeKey] || []).map((cat) => {
                      const ref = { type: typeKey, id: cat.id };
                      return (
                        <Pill key={cat.id} on={selCats.some((r) => sameRef(r, ref))} onClick={() => toggleCat(ref)}>{categoryName(typeKey, cat.id) || cat.name}</Pill>
                      );
                    })}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
          <Box sx={{ marginTop: "8px" }}>
            <FieldLabel sx={{ marginBottom: "6px" }}>{t("modal.targets.items_label")}</FieldLabel>
            <SearchAddPicker items={picker.items} onAdd={addItem} excludeKeys={selItems.map(keyOf)} categoryName={categoryName} />
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

// Persistent side "cart" — shows only the current + previous steps' info.
const Cart = ({ step, type, valueText, targetMode, selTypes, selCats, selItems, removeItem, bundleItems, removeBundle, conditionText, communitiesText, itemOf, categoryName, bundleQty }) => {
  const c = useTokens();
  const { t } = useTranslation("hatavot");
  // `qty` (bundle rows only): { value, onChange, error } shows the per-item
  // quantity input; { fixed: n } shows a read-only "×n" (single-item set deal).
  const itemCard = (ref, onRemove, qty) => {
    const it = itemOf(ref);
    return (
      <Box key={keyOf(ref)} sx={{ display: "flex", alignItems: "center", gap: "8px", padding: "7px 8px", border: `1px solid ${c.border}`, borderRadius: "8px", backgroundColor: c.surface }}>
        <ItemThumb item={it} size={34} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "13px", color: c.textStrong, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.name}</Typography>
          <Typography sx={{ fontSize: "11px", color: c.textSoft }}>#{it.id} · {t(`item_type.${it.type}`)}{it.price != null ? ` · ${CURRENCY}${it.price}` : ""}</Typography>
        </Box>
        {qty && qty.fixed != null && (
          <Typography sx={{ fontSize: "12.5px", fontWeight: 600, color: c.textMedium }}>×{qty.fixed}</Typography>
        )}
        {qty && qty.fixed == null && (
          <TextField
            size="small"
            value={qty.value}
            error={qty.error}
            onChange={(e) => qty.onChange(e.target.value.replace(/[^0-9]/g, ""))}
            placeholder="1"
            inputProps={{ inputMode: "numeric", min: 1, "aria-label": t("modal.bundle.item_qty_label"), style: { textAlign: "center", padding: "4px 2px", fontSize: "13px" } }}
            sx={{ width: "48px", flex: "none" }}
          />
        )}
        <IconButton size="small" onClick={onRemove}><CloseIcon sx={{ fontSize: 15, color: c.textFaint }} /></IconButton>
      </Box>
    );
  };
  const Row = ({ k, v }) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
      <Typography sx={{ fontSize: "12.5px", color: c.textSoft }}>{k}</Typography>
      <Typography sx={{ fontSize: "12.5px", fontWeight: 600, color: c.textStrong, textAlign: "end" }}>{v}</Typography>
    </Box>
  );
  const divider = <Box sx={{ height: "1px", backgroundColor: c.border }} />;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <ShoppingBagOutlinedIcon sx={{ fontSize: 18, color: c.brand }} />
        <Typography sx={{ fontSize: "15px", fontWeight: 600, color: c.textStrong }}>{t("modal.summary.title")}</Typography>
      </Box>

      <Row k={t("modal.summary.type")} v={t(`type_label.${type}`)} />

      {divider}
      <Box>
        <Typography sx={{ ...hintSx(c), marginBottom: "8px" }}>{type === "discount" ? t("modal.summary.applies_to") : t("modal.summary.bundle_includes")}</Typography>
        {type === "discount" ? (
          targetMode === "everything" ? (
            <Typography sx={{ fontSize: "13px", color: c.textStrong }}>{t("target.everything")}</Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                {selTypes.map((k) => (<Box key={k} sx={{ fontSize: "12px", padding: "3px 9px", borderRadius: "6px", backgroundColor: c.brandSoft, color: c.brand }}>{t("target.all_of_type", { type: t(`item_type.${k}`) })}</Box>))}
                {selCats.map((ref) => (<Box key={keyOf(ref)} sx={{ fontSize: "12px", padding: "3px 9px", borderRadius: "6px", backgroundColor: c.chipBg, color: c.textBody }}>{categoryName(ref.type, ref.id)}</Box>))}
              </Box>
              {selItems.map((ref) => itemCard(ref, () => removeItem(ref)))}
              {!selTypes.length && !selCats.length && !selItems.length && <Typography sx={hintSx(c)}>{t("target.none")}</Typography>}
            </Box>
          )
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {bundleItems.length ? bundleItems.map((ref) => itemCard(ref, () => removeBundle(ref), bundleQty?.cardQty(ref))) : <Typography sx={hintSx(c)}>{t("target.none_bundle")}</Typography>}
            {bundleQty?.sumText && (
              <Typography sx={{ fontSize: "12px", color: bundleQty.sumOk ? c.textSoft : c.danger }}>{bundleQty.sumText}</Typography>
            )}
          </Box>
        )}
      </Box>

      {step >= 1 && (
        <>
          {divider}
          <Row k={t("modal.summary.value")} v={valueText || "—"} />
          <Typography sx={{ fontSize: "12.5px", color: c.textMedium, lineHeight: 1.6 }}>{conditionText}</Typography>
        </>
      )}
      {step >= 2 && (
        <>
          {divider}
          <Row k={t("modal.summary.publish")} v={communitiesText} />
        </>
      )}
    </Box>
  );
};

const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const refsOf = (arr) => (arr || []).map((r) => ({ type: r.type, id: Number(r.id) }));

/**
 * Create / edit a הטבה. Builds the Admin_system `HatavaInput` from the modal
 * state and POSTs (create) or PUTs (edit) it; `editHatava` is a full
 * `SupplierHatava` row and hydrates every field (COMY1-359).
 */
const CreateHatavaModal = ({ open, onClose, onSaved, editHatava = null }) => {
  const c = useTokens();
  const { t } = useTranslation("hatavot");
  const snackbar = useSnackbar();
  const isMobile = useMediaQuery("(max-width:767px)");
  const isEdit = !!editHatava;
  const STEPS = [t("modal.steps.type_items"), t("modal.steps.conditions"), t("modal.steps.publish")];

  // ---- picker (the supplier's catalog + communities) -----------------------
  const [picker, setPicker] = useState(EMPTY_PICKER);
  const [pickerLoading, setPickerLoading] = useState(true);
  const [pickerError, setPickerError] = useState(false);
  const fetchPicker = useCallback(async () => {
    try {
      setPickerLoading(true);
      setPickerError(false);
      const response = await apiGetRequest(endpoints.hatavot.supplierPicker);
      if (response.status === 200) setPicker({ ...EMPTY_PICKER, ...(response.data || {}) });
    } catch (error) {
      console.error("Error fetching hatavot picker:", error);
      setPickerError(true);
    } finally {
      setPickerLoading(false);
    }
  }, []);
  useEffect(() => {
    fetchPicker();
  }, [fetchPicker]);

  // ---- form state (hydrated from the edited row) ---------------------------
  const [step, setStep] = useState(0);
  const [type, setType] = useState(() => (editHatava ? uiType(editHatava) : "discount"));
  const [itemSource, setItemSource] = useState("existing");
  const [targetMode, setTargetMode] = useState(() => editHatava?.targets?.mode || "everything");
  const [selTypes, setSelTypes] = useState(() => editHatava?.targets?.types || []);
  const [selCats, setSelCats] = useState(() => refsOf(editHatava?.targets?.categories));
  const [selItems, setSelItems] = useState(() => refsOf(editHatava?.targets?.items));
  const [bundleStyle, setBundleStyle] = useState(() => bundleStyleOf(editHatava?.kind));
  const [bundleItems, setBundleItems] = useState(() => refsOf(editHatava?.bundleItems));
  // Per-item quantity inside a set deal, keyed by keyOf(ref) -> raw input string.
  const [bundleQty, setBundleQty] = useState(() => {
    const map = {};
    [...(editHatava?.bundleItems || []), ...(editHatava?.bundleItemsDetailed || [])].forEach((r) => {
      if (r?.quantity != null && Number(r.quantity) >= 1) map[keyOf(r)] = String(r.quantity);
    });
    return map;
  });

  const [valueKind, setValueKind] = useState(() => (editHatava?.kind === "discount" ? editHatava.valueType : "percent"));
  const [valueAmount, setValueAmount] = useState(() => (editHatava?.kind === "discount" ? toStr(editHatava.valueAmount) : ""));
  const [bundlePrice, setBundlePrice] = useState(() => (editHatava?.kind === "bundle" ? toStr(editHatava.valueAmount) : ""));
  const [buyQty, setBuyQty] = useState(() => (editHatava?.kind === "quantity" ? toStr(editHatava.buyQty) : ""));
  const [payQty, setPayQty] = useState(() => (editHatava?.kind === "quantity" ? toStr(editHatava.payQty) : ""));
  const [unlock, setUnlock] = useState(() => editHatava?.unlock || "automatic");
  const [code, setCode] = useState(() => editHatava?.couponCode || "");
  const [dateFrom, setDateFrom] = useState(() => (editHatava?.validFrom ? String(editHatava.validFrom).slice(0, 10) : toLocalISO(new Date())));
  const [dateTo, setDateTo] = useState(() => {
    if (editHatava?.validUntil) return String(editHatava.validUntil).slice(0, 10);
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    return toLocalISO(d);
  });
  const [totalRedemptions, setTotalRedemptions] = useState(() => toStr(editHatava?.maxRedemptions));
  const [perMemberCap, setPerMemberCap] = useState(() => toStr(editHatava?.perMemberCap));
  const [minSpend, setMinSpend] = useState(() => toStr(editHatava?.minSpend));

  const [communityScope, setCommunityScope] = useState(() => editHatava?.communityScope || "all");
  const [specificCommunities, setSpecificCommunities] = useState(() => (editHatava?.communityIds || []).map(Number));
  const [autoFuture, setAutoFuture] = useState(() => editHatava?.autoAddFuture ?? true);
  const [hatavaTerms, setHatavaTerms] = useState(() => editHatava?.terms || "");
  const [title, setTitle] = useState(() => editHatava?.title || "");
  const [titleTouched, setTitleTouched] = useState(isEdit);

  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null); // i18n key (modal.*)
  const [codeError, setCodeError] = useState(null); // i18n key for the code field

  // ---- "new item": the real product modal --------------------------------
  const [newVertical, setNewVertical] = useState("products");
  const [productModal, setProductModal] = useState({ open: false, loading: false, categories: [], tags: [] });

  useEffect(() => {
    const creatable = CREATABLE_TYPES.filter((k) => picker.itemTypes.includes(k));
    if (creatable.length && !creatable.includes(newVertical)) setNewVertical(creatable[0]);
  }, [picker.itemTypes, newVertical]);

  const openProductModal = async () => {
    const api = endpoints.catalog(newVertical);
    try {
      setProductModal((p) => ({ ...p, loading: true }));
      const [catRes, tagRes] = await Promise.all([
        apiGetRequest(api.getCategories),
        apiGetRequest(api.getAllTags).catch(() => ({ data: { tags: [] } })),
      ]);
      setProductModal({ open: true, loading: false, categories: catRes.data?.categories || [], tags: tagRes.data?.tags || [] });
    } catch (error) {
      console.error("Error loading product modal data:", error);
      setProductModal((p) => ({ ...p, loading: false }));
      snackbar(t("modal.errors.generic"), "error");
    }
  };

  const handleProductSaved = async (saved) => {
    setProductModal((p) => ({ ...p, open: false }));
    if (!saved?.id) return;
    const ref = { type: newVertical, id: Number(saved.id) };
    // Make the new row findable by the pickers/cart, then select it.
    await fetchPicker();
    if (type === "discount") {
      setTargetMode("custom");
      setSelItems((prev) => (prev.some((r) => sameRef(r, ref)) ? prev : [...prev, ref]));
    } else {
      setBundleItems((prev) => (prev.some((r) => sameRef(r, ref)) ? prev : [...prev, ref]));
    }
    setItemSource("existing");
    snackbar(t("modal.new_item.created"), "success");
  };

  // ---- lookups --------------------------------------------------------------
  // Picker rows first; items that are no longer active (so not in the picker)
  // fall back to the names the edited row carries.
  const itemsByKey = useMemo(() => {
    const map = {};
    (editHatava?.targetSummary?.items || []).forEach((it) => { map[keyOf(it)] = { ...it, price: null, image: null, categoryId: null }; });
    (editHatava?.bundleItemsDetailed || []).forEach((it) => { map[keyOf(it)] = { ...it, categoryId: null }; });
    picker.items.forEach((it) => { map[keyOf(it)] = it; });
    return map;
  }, [picker.items, editHatava]);
  const itemOf = useCallback((ref) => itemsByKey[keyOf(ref)] || { ...ref, name: `#${ref.id}`, price: null, image: null }, [itemsByKey]);
  const categoryName = useCallback((typeKey, id) => {
    const fromPicker = (picker.categories[typeKey] || []).find((cat) => Number(cat.id) === Number(id));
    if (fromPicker) return fromPicker.name;
    const fromEdit = (editHatava?.targetSummary?.categories || []).find((cat) => cat.type === typeKey && Number(cat.id) === Number(id));
    return fromEdit?.name || "";
  }, [picker.categories, editHatava]);
  const bundleCandidates = useMemo(() => picker.items.filter((it) => CREATABLE_TYPES.includes(it.type)), [picker.items]);

  // ---- selection helpers ----------------------------------------------------
  const toggleRef = (setArr) => (ref) => setArr((prev) => (prev.some((r) => sameRef(r, ref)) ? prev.filter((r) => !sameRef(r, ref)) : [...prev, ref]));
  const toggleWholeType = (typeKey) => {
    setSelTypes((prev) => (prev.includes(typeKey) ? prev.filter((k) => k !== typeKey) : [...prev, typeKey]));
    setSelCats((prev) => prev.filter((r) => r.type !== typeKey));
  };
  const toggleCat = (ref) => {
    toggleRef(setSelCats)(ref);
    setSelTypes((prev) => prev.filter((k) => k !== ref.type));
  };
  const toggleCommunity = (id) => setSpecificCommunities((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  // ---- derived ----------------------------------------------------------------
  const isDiscount = type === "discount";
  const kind = isDiscount ? "discount" : bundleStyle === "combined" ? "bundle" : bundleStyle;
  const valueType = isDiscount ? valueKind : kind;
  const draft = {
    valueType,
    valueAmount: isDiscount ? numOrNull(valueAmount) : kind === "bundle" ? numOrNull(bundlePrice) : null,
    buyQty: kind === "bogo" ? 2 : kind === "quantity" ? numOrNull(buyQty) : null,
    payQty: kind === "bogo" ? 1 : kind === "quantity" ? numOrNull(payQty) : null,
  };
  const generatedTitle = autoTitle(draft, t);
  const effectiveTitle = titleTouched && title.trim() ? title : generatedTitle;
  const valueText = draft.valueAmount || draft.buyQty ? valueLabel(draft, t) : "";
  const communitiesText = communityScope === "all" ? t("modal.summary.all_communities") : t("modal.summary.communities_count", { count: specificCommunities.length });
  const conditionText = `${t("modal.summary.valid", { from: fmtDate(dateFrom), to: fmtDate(dateTo) })} · ${t(unlock === "code" ? "modal.summary.unlock_code" : "modal.summary.unlock_automatic")}`;

  // ---- set-deal quantities ------------------------------------------------------
  // 'quantity' (N-for-M) and 'bogo' deals form a set of buyQty (bogo: 2) units.
  // One item -> the whole set is that item (quantity = set size, no input).
  // Several items -> per-item quantities that must add up to the set size.
  // 'combined' bundles: optional per-item quantity (default 1).
  const isSetKind = kind === "bogo" || kind === "quantity";
  const setSize = kind === "bogo" ? 2 : kind === "quantity" ? draft.buyQty : null;
  const singleSetItem = isSetKind && bundleItems.length === 1;
  const rawQty = (ref) => bundleQty[keyOf(ref)] ?? "";
  const qtyOf = (ref) => {
    const raw = rawQty(ref);
    if (raw === "") return 1; // empty input = the "1" placeholder
    const n = Number(raw);
    return Number.isInteger(n) && n >= 1 ? n : null;
  };
  const qtySum = bundleItems.reduce((s, r) => s + (qtyOf(r) || 0), 0);
  const setSizeKnown = setSize > 0;
  const qtyInvalid = !isDiscount && !singleSetItem && bundleItems.some((r) => qtyOf(r) === null);
  const setSumOk = !isSetKind || singleSetItem || !setSizeKnown || qtySum === setSize;
  const bundleQtyView = isDiscount ? null : {
    cardQty: (ref) => (singleSetItem
      ? (setSizeKnown ? { fixed: setSize } : null)
      : { value: rawQty(ref), error: qtyOf(ref) === null || (isSetKind && !setSumOk), onChange: (v) => setBundleQty((m) => ({ ...m, [keyOf(ref)]: v })) }),
    sumText: isSetKind && bundleItems.length > 1
      ? (setSizeKnown ? t("modal.bundle.set_sum", { sum: qtySum, size: setSize }) : t("modal.bundle.set_sum_unknown", { sum: qtySum }))
      : "",
    sumOk: setSumOk,
  };

  // ---- validation per step ------------------------------------------------------
  const validateStep = (s) => {
    if (s === 0) {
      if (isDiscount) {
        if (targetMode === "custom" && !selTypes.length && !selCats.length && !selItems.length) return "modal.validation.items_required";
      } else if (kind === "bundle" && bundleItems.length < 2) return "modal.validation.bundle_items_required";
      else if (bundleItems.length < 1) return "modal.validation.bundle_item_required";
      if (qtyInvalid) return "modal.validation.item_qty_invalid";
      // bogo's set size (2) is known here; 'quantity' is checked once buyQty is set (step 1).
      if (kind === "bogo" && !setSumOk) return "modal.validation.set_size_mismatch";
    }
    if (s === 1) {
      if (["percent", "fixed", "price", "bundle"].includes(valueType) && !(draft.valueAmount > 0)) return "modal.validation.value_required";
      if (valueType === "percent" && draft.valueAmount > 100) return "modal.validation.percent_range";
      if (valueType === "quantity" && !(draft.buyQty > 0 && draft.payQty > 0 && draft.buyQty > draft.payQty)) return "modal.validation.quantity_invalid";
      if (kind === "quantity" && !setSumOk) return "modal.validation.set_size_mismatch";
      if (unlock === "code" && !code.trim()) return "modal.validation.code_required";
      if (!dateFrom || !dateTo || dateTo < dateFrom) return "modal.validation.dates_invalid";
    }
    if (s === 2) {
      if (communityScope === "specific" && !specificCommunities.length) return "modal.validation.communities_required";
    }
    return null;
  };

  const back = () => {
    setFormError(null);
    if (step === 0) onClose();
    else setStep(step - 1);
  };

  const buildPayload = () => {
    const emptyTargets = { mode: "everything", types: [], categories: [], items: [] };
    return {
      kind,
      valueType,
      valueAmount: draft.valueAmount,
      buyQty: draft.buyQty,
      payQty: draft.payQty,
      title: effectiveTitle,
      description: editHatava?.description ?? null,
      targets: isDiscount
        ? targetMode === "everything" ? emptyTargets : { mode: "custom", types: selTypes, categories: selCats, items: selItems }
        : emptyTargets,
      bundleItems: isDiscount ? [] : bundleItems.map((r) => ({ type: r.type, id: r.id, quantity: singleSetItem ? setSize : qtyOf(r) || 1 })),
      unlock,
      couponCode: unlock === "code" ? code.trim() : null,
      validFrom: dateFrom,
      validUntil: dateTo,
      minSpend: numOrNull(minSpend),
      maxRedemptions: numOrNull(totalRedemptions),
      perMemberCap: numOrNull(perMemberCap),
      stock: editHatava?.stock ?? null,
      terms: hatavaTerms.trim() || null,
      communityScope,
      communityIds: communityScope === "specific" ? specificCommunities : [],
      autoAddFuture: autoFuture,
    };
  };

  const save = async () => {
    try {
      setSaving(true);
      setFormError(null);
      setCodeError(null);
      const payload = buildPayload();
      if (isEdit) await apiPutRequest(endpoints.hatavot.supplierOne(editHatava.id), payload);
      else await apiPostRequest(endpoints.hatavot.supplierList, payload);
      onSaved?.();
      onClose();
    } catch (error) {
      console.error("Error saving hatava:", error);
      const apiCode = error?.response?.data?.code;
      if (apiCode === "COUPON_CODE_TAKEN") {
        setCodeError("modal.errors.COUPON_CODE_TAKEN");
        setStep(1);
      } else {
        setFormError(apiCode && t(`modal.errors.${apiCode}`, { defaultValue: "" }) ? `modal.errors.${apiCode}` : "modal.errors.generic");
      }
    } finally {
      setSaving(false);
    }
  };

  const next = () => {
    // On the last step re-check every step — the cart (items + quantities)
    // stays editable throughout, so an earlier step may have become invalid.
    const toCheck = step === 2 ? [0, 1, 2] : [step];
    for (const s of toCheck) {
      const err = validateStep(s);
      if (err) {
        setFormError(err);
        if (s !== step) setStep(s);
        return;
      }
    }
    setFormError(null);
    if (step === 2) save();
    else setStep(step + 1);
  };

  // formik-shaped shim so we can reuse the app's CustomDatePicker.
  const dateFormik = {
    setFieldValue: (n, v) => (n === "dateFrom" ? setDateFrom(v) : setDateTo(v)),
    setFieldTouched: () => {},
    handleBlur: () => {},
  };
  const asDate = (iso) => (iso ? new Date(`${iso}T00:00:00`) : null);

  const isNew = itemSource === "new";
  const currencyAdornment = (side) => <Box component="span" sx={{ [side]: "12px", color: c.textMedium }}>{CURRENCY}</Box>;
  const pickerEmpty = !pickerLoading && !pickerError && picker.itemTypes.length === 0;

  return (
    <Dialog open={open} fullScreen={isMobile} onClose={(_e, reason) => { if (reason === "backdropClick") return; onClose(); }}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : "12px", maxWidth: "960px", width: "100%", maxHeight: isMobile ? "100%" : "92vh", display: "flex", flexDirection: "column" } }}>
      {/* Header */}
      <Box sx={{ px: "24px", py: "18px", borderBottom: `1px solid ${c.border}`, flexShrink: 0 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "16px" }}>
          <Typography sx={{ fontSize: "18px", fontWeight: 500, color: c.textStrong }}>{isEdit ? t("modal.title_edit") : t("modal.title_create")}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" sx={{ color: c.iconMuted }} /></IconButton>
        </Box>
        <StepperTabs activeStep={step} steps={STEPS} />
      </Box>

      {/* Body: content + persistent cart */}
      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, flex: 1, minHeight: 0, overflowY: { xs: "auto", md: "visible" } }}>
        <Box sx={{ flex: 1, minWidth: 0, px: "24px", py: "22px", maxHeight: { md: "62vh" }, overflowY: { md: "auto" } }}>
          {formError && (
            <Alert severity="error" onClose={() => setFormError(null)} sx={{ mb: "16px", borderRadius: "8px" }}>{t(formError, { size: setSize })}</Alert>
          )}

          {step === 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "22px" }}>
              <Box>
                <LabelWithHelp help={t("modal.type.help")}>{t("modal.type.label")}</LabelWithHelp>
                <Segmented value={type} onChange={setType} options={[{ value: "discount", label: t("modal.type.discount") }, { value: "bundle", label: t("modal.type.bundle") }]} />
              </Box>

              {pickerLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", padding: "32px 0" }}><CircularProgress size={36} sx={{ color: c.statusActive }} /></Box>
              ) : pickerError ? (
                <Box sx={{ textAlign: "center", padding: "24px", border: `1px dashed ${c.border}`, borderRadius: "10px" }}>
                  <Typography sx={{ ...hintSx(c), marginBottom: "10px" }}>{t("modal.picker_error")}</Typography>
                  <Button onClick={fetchPicker} variant="outlined" sx={{ textTransform: "none", borderRadius: "8px", color: c.brand, borderColor: c.brand }}>{t("list.retry")}</Button>
                </Box>
              ) : (
                <>
                  {pickerEmpty && <Alert severity="info" sx={{ borderRadius: "8px" }}>{t("modal.picker_empty")}</Alert>}
                  <Box>
                    <FieldLabel>{t("modal.source.label")}</FieldLabel>
                    <Segmented value={itemSource} onChange={setItemSource} options={[{ value: "existing", label: t("modal.source.existing") }, { value: "new", label: t("modal.source.new") }]} />
                  </Box>

                  <Collapse in={isNew} unmountOnExit>
                    <NewItemPanel itemTypes={picker.itemTypes} vertical={newVertical} setVertical={setNewVertical} onOpen={openProductModal} loading={productModal.loading} />
                  </Collapse>

                  <Collapse in={!isNew && isDiscount} unmountOnExit>
                    <TargetPicker mode={targetMode} setMode={setTargetMode} picker={picker} selTypes={selTypes} selCats={selCats} toggleWholeType={toggleWholeType} toggleCat={toggleCat} selItems={selItems} addItem={toggleRef(setSelItems)} categoryName={categoryName} />
                  </Collapse>

                  <Collapse in={!isNew && !isDiscount} unmountOnExit>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                      <Box>
                        <LabelWithHelp help={t("modal.bundle.style_help")}>{t("modal.bundle.style_label")}</LabelWithHelp>
                        <Segmented value={bundleStyle} onChange={setBundleStyle} options={[{ value: "combined", label: t("modal.bundle.combined") }, { value: "bogo", label: t("modal.bundle.bogo") }, { value: "quantity", label: t("modal.bundle.quantity") }]} />
                      </Box>
                      <Box>
                        <FieldLabel>{t("modal.bundle.items_label")}</FieldLabel>
                        <SearchAddPicker items={bundleCandidates} onAdd={toggleRef(setBundleItems)} excludeKeys={bundleItems.map(keyOf)} categoryName={categoryName} />
                        {bundleItems.length > 1 && (
                          <Typography sx={{ ...hintSx(c), marginTop: "6px" }}>{t(isSetKind ? "modal.bundle.qty_hint_set" : "modal.bundle.qty_hint_combined")}</Typography>
                        )}
                      </Box>
                    </Box>
                  </Collapse>
                </>
              )}
            </Box>
          )}

          {step === 1 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "22px" }}>
              {isDiscount ? (
                <Box>
                  <LabelWithHelp help={t("modal.discount.kind_help")}>{t("modal.discount.kind_label")}</LabelWithHelp>
                  <Segmented value={valueKind} onChange={setValueKind} options={[{ value: "percent", label: t("modal.discount.percent") }, { value: "fixed", label: t("modal.discount.fixed", { currency: CURRENCY }) }, { value: "price", label: t("modal.discount.price") }]} />
                  <Box sx={{ marginTop: "10px", maxWidth: "220px" }}>
                    <InputField label={valueKind === "price" ? t("modal.discount.price_label") : t("modal.discount.amount_label")} name="valueAmount" type="number" value={valueAmount} onChange={(e) => setValueAmount(e.target.value)}
                      InputProps={{ endAdornment: <Box component="span" sx={{ pr: "12px", color: c.textMedium }}>{valueKind === "percent" ? "%" : CURRENCY}</Box> }} />
                  </Box>
                </Box>
              ) : kind === "bundle" ? (
                <Box sx={{ maxWidth: "260px" }}>
                  <InputField label={t("modal.bundle.price_label")} name="bundlePrice" type="number" value={bundlePrice} onChange={(e) => setBundlePrice(e.target.value)} InputProps={{ startAdornment: currencyAdornment("pl") }} />
                </Box>
              ) : kind === "bogo" ? (
                <Typography sx={hintSx(c)}>{t("modal.bundle.bogo_hint")}</Typography>
              ) : (
                <Box>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", maxWidth: "320px" }}>
                    <InputField label={t("modal.bundle.buy_label")} name="buyQty" type="number" value={buyQty} onChange={(e) => setBuyQty(e.target.value)} />
                    <InputField label={t("modal.bundle.pay_label")} name="payQty" type="number" value={payQty} onChange={(e) => setPayQty(e.target.value)} />
                  </Box>
                  <Typography sx={{ ...hintSx(c), marginTop: "6px" }}>{t("modal.bundle.quantity_hint")}</Typography>
                </Box>
              )}

              <Box sx={{ height: "1px", backgroundColor: c.divider }} />

              <Box>
                <LabelWithHelp help={t("modal.unlock.help")}>{t("modal.unlock.label")}</LabelWithHelp>
                <Segmented value={unlock} onChange={(v) => { setUnlock(v); setCodeError(null); }} options={[{ value: "automatic", label: t("modal.unlock.automatic") }, { value: "code", label: t("modal.unlock.code") }]} />
                <Collapse in={unlock === "code"} unmountOnExit>
                  <Box sx={{ marginTop: "10px", maxWidth: "220px" }}>
                    <InputField label={t("modal.unlock.code_label")} name="code" value={code} onChange={(e) => { setCode(e.target.value); setCodeError(null); }} placeholder={t("modal.unlock.code_placeholder")} error={!!codeError} helperText={codeError ? t(codeError) : ""} />
                  </Box>
                </Collapse>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "16px" }}>
                <CustomDatePicker name="dateFrom" label={t("modal.dates.from")} selected={asDate(dateFrom)} maxDate={FAR_FUTURE} dateFormat="dd/MM/yyyy" showYearDropdown formik={dateFormik} />
                <CustomDatePicker name="dateTo" label={t("modal.dates.to")} selected={asDate(dateTo)} maxDate={FAR_FUTURE} dateFormat="dd/MM/yyyy" showYearDropdown formik={dateFormik} />
                <Box>
                  <LabelWithHelp help={t("modal.caps.max_help")}>{t("modal.caps.max_label")}</LabelWithHelp>
                  <InputField name="totalRedemptions" type="number" value={totalRedemptions} onChange={(e) => setTotalRedemptions(e.target.value)} placeholder={t("modal.caps.unlimited")} />
                </Box>
                <InputField label={t("modal.caps.per_member_label")} name="perMemberCap" type="number" value={perMemberCap} onChange={(e) => setPerMemberCap(e.target.value)} placeholder={t("modal.caps.unlimited")} />
                <InputField label={t("modal.caps.min_spend_label")} name="minSpend" type="number" value={minSpend} onChange={(e) => setMinSpend(e.target.value)} placeholder={t("modal.caps.none")} InputProps={{ startAdornment: currencyAdornment("pl") }} />
              </Box>
            </Box>
          )}

          {step === 2 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              <Box sx={{ maxWidth: "420px" }}>
                <LabelWithHelp help={t("modal.publish.title_help")}>{t("modal.publish.title_label")}</LabelWithHelp>
                <InputField name="title" value={effectiveTitle} onChange={(e) => { setTitle(e.target.value); setTitleTouched(e.target.value.trim().length > 0); }} />
              </Box>

              <Box>
                <FieldLabel>{t("modal.publish.where_label")}</FieldLabel>
                <Segmented value={communityScope} onChange={setCommunityScope} options={[{ value: "all", label: t("modal.publish.all_communities") }, { value: "specific", label: t("modal.publish.specific_communities") }]} />
                <Collapse in={communityScope === "specific"} unmountOnExit>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "10px" }}>
                    {picker.communities.length === 0 && <Typography sx={hintSx(c)}>{t("modal.publish.no_communities")}</Typography>}
                    {picker.communities.map((com) => (<Pill key={com.id} on={specificCommunities.includes(Number(com.id))} onClick={() => toggleCommunity(Number(com.id))}>{com.name}</Pill>))}
                  </Box>
                </Collapse>
                {/* Auto-add communities the supplier joins later — so they don't
                    have to revisit each הטבה. Default on. */}
                <Box component="label" sx={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "14px", cursor: "pointer" }}>
                  <Checkbox checked={autoFuture} onChange={(e) => setAutoFuture(e.target.checked)} size="small" sx={{ p: 0, color: c.textFaint, "&.Mui-checked": { color: c.brand } }} />
                  <Typography sx={{ fontSize: "14px", color: c.textStrong }}>{t("modal.publish.auto_future")}</Typography>
                </Box>
              </Box>

              <Box>
                <LabelWithHelp help={t("modal.publish.terms_help")}>{t("modal.publish.terms_label")}</LabelWithHelp>
                <InputField name="hatavaTerms" value={hatavaTerms} onChange={(e) => setHatavaTerms(e.target.value)} multiline rows={3} placeholder={t("modal.publish.terms_placeholder")} />
              </Box>

              <Box sx={{ display: "flex", gap: "10px", alignItems: "flex-start", padding: "12px 14px", borderRadius: "10px", backgroundColor: c.brandSoft, border: `1px solid ${c.brandBorder}` }}>
                <InfoOutlinedIcon sx={{ fontSize: 20, color: c.brand, mt: "1px" }} />
                <Typography sx={{ fontSize: "13px", color: c.textStrong, lineHeight: 1.5 }}>{t("modal.publish.visibility_note")}</Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* Persistent cart */}
        <Box sx={{ width: { xs: "100%", md: "300px" }, flex: "none", borderTop: { xs: `1px solid ${c.border}`, md: "none" }, borderInlineStart: { md: `1px solid ${c.border}` }, backgroundColor: c.surfaceMuted, px: "18px", py: "20px", maxHeight: { md: "62vh" }, overflowY: { md: "auto" } }}>
          <Cart step={step} type={type} valueText={valueText} targetMode={targetMode} selTypes={selTypes} selCats={selCats} selItems={selItems} removeItem={toggleRef(setSelItems)} bundleItems={bundleItems} removeBundle={toggleRef(setBundleItems)} conditionText={conditionText} communitiesText={communitiesText} itemOf={itemOf} categoryName={categoryName} bundleQty={bundleQtyView} />
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "16px", px: "24px", py: "18px", borderTop: `1px solid ${c.border}`, flexShrink: 0 }}>
        <Button onClick={back} disabled={saving} variant="contained" sx={{ textTransform: "none", fontSize: "15px", fontWeight: 500, borderRadius: "8px", minWidth: "130px", backgroundColor: c.neutralButton, color: c.surface, boxShadow: "none", "&:hover": { backgroundColor: c.neutralButtonHover, boxShadow: "none" } }}>
          {step === 0 ? t("modal.buttons.cancel") : t("modal.buttons.back")}
        </Button>
        <Button onClick={next} disabled={saving || pickerLoading} variant="contained" sx={{ textTransform: "none", fontSize: "15px", fontWeight: 500, borderRadius: "8px", minWidth: "130px", backgroundColor: c.brand, boxShadow: "none", "&:hover": { backgroundColor: c.brandHover, boxShadow: "none" } }}>
          {saving ? t("modal.buttons.saving") : step === 2 ? (isEdit ? t("modal.buttons.save") : t("modal.buttons.create")) : t("modal.buttons.next")}
        </Button>
      </Box>

      {/* The real product/restaurant creation modal (Products tab) — the created
          item is added to this deal in handleProductSaved. */}
      {productModal.open && (
        <ProductModal
          vertical={newVertical}
          open={productModal.open}
          onClose={() => setProductModal((p) => ({ ...p, open: false }))}
          product={null}
          isEdit={false}
          isDuplicate={false}
          categories={productModal.categories}
          tagSuggestions={productModal.tags}
          onProductSaved={handleProductSaved}
        />
      )}
    </Dialog>
  );
};

export default CreateHatavaModal;
